// Pure helpers for the permission catalogue: group permissions by domain and
// order them deterministically. Shared by the Permissions screen and the
// role-permission assignment drawer, and unit-tested in isolation.
//
// Two levels of grouping are offered. `groupByDomain` is the flat "one section
// per domain" view; `groupByAreaAndDomain` nests domains under a top-level
// "area" so the POS keys (all sharing the stored domain "pos") fan back out into
// their real sub-domains. Area and sub-domain are DERIVED FROM THE KEY, because
// the stored `domain` is unreliable for POS (every `pos.*` key stores "pos").

import type { PermissionDto } from "../api/types.js"

export interface PermissionGroup {
  domain: string
  permissions: PermissionDto[]
}

export interface AreaGroup {
  area: string
  domains: PermissionGroup[]
}

/**
 * Proposed area map — adjust freely; POS is derived from the `pos.` prefix, so it
 * never appears here. Maps a merch/platform key's leading segment to a display
 * area. This is a pure client-side display config: it carries no backend, token
 * or permission meaning and renaming an area here renames nothing on the wire.
 */
export const DOMAIN_AREA_MAP: Record<string, string> = {
  shops: "centrale",
  signals: "centrale",
  analytics: "centrale",
  iam: "platform",
  roles: "platform",
  teams: "platform",
  users: "platform",
  org: "merch"
}

/** Fixed display order for the known areas; unknown areas sort after, then A→Z. */
const AREA_RANK = ["pos", "centrale", "merch", "platform"]

function areaRank(area: string): number {
  const index = AREA_RANK.indexOf(area)
  return index === -1 ? AREA_RANK.length : index
}

/**
 * Derive the top-level area from a permission key. `pos.*` keys are POS; every
 * other key is looked up by its first segment in DOMAIN_AREA_MAP, defaulting to
 * the merch area.
 */
export function deriveArea(key: string): string {
  if (key.startsWith("pos.")) return "pos"
  const first = key.split(".")[0] ?? key
  return DOMAIN_AREA_MAP[first] ?? "merch"
}

/**
 * Derive the sub-domain from a permission key. For a POS key `pos.<domain>.<...>`
 * the sub-domain is the second segment; for every other key it is the first
 * segment (so `org.settings.edit` → `org`).
 */
export function deriveDomain(key: string): string {
  const parts = key.split(".")
  if (key.startsWith("pos.")) return parts[1] ?? "pos"
  return parts[0] ?? key
}

/** Group permissions by `domain`, domains sorted A→Z, keys sorted A→Z within. */
export function groupByDomain(permissions: PermissionDto[]): PermissionGroup[] {
  const byDomain = new Map<string, PermissionDto[]>()
  for (const perm of permissions) {
    const list = byDomain.get(perm.domain)
    if (list) {
      list.push(perm)
    } else {
      byDomain.set(perm.domain, [perm])
    }
  }
  return [...byDomain.entries()]
    .map(([domain, perms]) => ({
      domain,
      permissions: [...perms].sort((a, b) => a.key.localeCompare(b.key))
    }))
    .sort((a, b) => a.domain.localeCompare(b.domain))
}

/**
 * Group permissions into a two-level Area → Domain tree, deriving both levels
 * from each key. Areas are ordered by AREA_RANK (unknown areas after, A→Z),
 * sub-domains A→Z within an area, and keys A→Z within a sub-domain.
 */
export function groupByAreaAndDomain(permissions: PermissionDto[]): AreaGroup[] {
  const byArea = new Map<string, Map<string, PermissionDto[]>>()
  for (const perm of permissions) {
    const area = deriveArea(perm.key)
    const domain = deriveDomain(perm.key)
    let domains = byArea.get(area)
    if (!domains) {
      domains = new Map()
      byArea.set(area, domains)
    }
    const list = domains.get(domain)
    if (list) {
      list.push(perm)
    } else {
      domains.set(domain, [perm])
    }
  }
  return [...byArea.entries()]
    .map(([area, domains]) => ({
      area,
      domains: [...domains.entries()]
        .map(([domain, perms]) => ({
          domain,
          permissions: [...perms].sort((a, b) => a.key.localeCompare(b.key))
        }))
        .sort((a, b) => a.domain.localeCompare(b.domain))
    }))
    .sort((a, b) => areaRank(a.area) - areaRank(b.area) || a.area.localeCompare(b.area))
}

/** Translate a domain key via the dictionary, falling back to the raw key. */
export function domainLabel(domain: string, dict: Record<string, string>): string {
  return dict[domain] ?? domain
}

/**
 * Normalize text for case- and accent-insensitive matching: strip diacritics via
 * NFD decomposition, lowercase, and trim. `normalizeText("Rôle") === "role"`.
 */
export function normalizeText(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim()
}

/** Criteria for {@link filterPermissions}; every field is optional. */
export interface PermissionFilter {
  /** Restrict to a single area key; null/undefined/"" means every area. */
  area?: string | null
  /** Free-text query matched against key, description and localized domain label. */
  query?: string
  /** Domain label dictionary so the query can match the translated domain name. */
  domainDict?: Record<string, string>
}

/**
 * Narrow an Area → Domain tree by area and/or free-text query. Pure and
 * deterministic: it preserves the input ordering, matches on individual
 * permissions (a domain survives only if it keeps ≥1 permission, an area only if
 * it keeps ≥1 domain), and normalizes both sides of the text comparison so it is
 * case- and accent-insensitive. A query that matches a domain's localized label
 * keeps every permission in that domain.
 */
export function filterPermissions(groups: AreaGroup[], filter: PermissionFilter = {}): AreaGroup[] {
  const area = filter.area || null
  const domainDict = filter.domainDict ?? {}
  const query = normalizeText(filter.query ?? "")

  return groups
    .filter((group) => area === null || group.area === area)
    .map((group) => ({
      area: group.area,
      domains: group.domains
        .map((domain) => {
          if (query === "") return domain
          const domainMatches = normalizeText(domainLabel(domain.domain, domainDict)).includes(
            query
          )
          const permissions = domain.permissions.filter(
            (p) =>
              domainMatches ||
              normalizeText(p.key).includes(query) ||
              normalizeText(p.description ?? "").includes(query)
          )
          return { domain: domain.domain, permissions }
        })
        .filter((domain) => domain.permissions.length > 0)
    }))
    .filter((group) => group.domains.length > 0)
}

/** Translate an area key via the dictionary, falling back to the raw key. */
export function areaLabel(area: string, dict: Record<string, string>): string {
  return dict[area] ?? area
}

/**
 * Diff a desired permission-id selection against the currently-assigned set.
 * Returns whether anything changed — used to enable/disable the Save button.
 */
export function selectionChanged(current: string[], desired: Set<string>): boolean {
  if (current.length !== desired.size) return true
  return current.some((id) => !desired.has(id))
}
